import { useEffect, useState, type FormEvent } from 'react'
import { ApiClientError } from '../../services/apiClient'
import { adminClient, type AdminPlan, type AdminPlanPayload } from '../../services/adminClient'
import { Modal } from '../../components/Modal'

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; plan: AdminPlan }

/**
 * Gestión de planes (§12.6 SRS).
 * Convención visual: -1 = "Ilimitado", 0 = "No incluido", >0 = número.
 */
export function PlanesPage() {
  const [items, setItems] = useState<AdminPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })

  function reload() {
    setLoading(true)
    adminClient.planes.list()
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { reload() }, [])

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Planes de suscripción</h1>
          <span className="muted">{items.length} planes</span>
        </div>
        <div className="list-toolbar__actions">
          <button type="button" className="primary-btn" onClick={() => setMode({ kind: 'create' })}>
            + Nuevo plan
          </button>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Mensual</th>
              <th>Anual</th>
              <th>Reportes/mes</th>
              <th>Mejoras IA/mes</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="data-table__empty">No hay planes.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="data-table__row" tabIndex={0}
                  onClick={() => setMode({ kind: 'edit', plan: p })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode({ kind: 'edit', plan: p }) } }}>
                <td>
                  <strong>{p.name}</strong>
                  {p.recommended && <span className="badge badge--pdf_generated" style={{ marginLeft: '.5rem' }}>Recomendado</span>}
                {p.is_default && <span className="badge badge--draft" style={{ marginLeft: '.5rem' }}>Default</span>}
                </td>
                <td className="mono">${p.monthly_price.toLocaleString('es-MX')}</td>
                <td className="mono">${p.yearly_price.toLocaleString('es-MX')}</td>
                <td>{limitLabel(p.max_reports_per_month)}</td>
                <td>{limitLabel(p.max_ai_enhancements_per_month)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                    <span className={`badge ${p.active ? 'badge--pdf_generated' : 'badge--draft'}`}>
                      {p.active ? 'Activo' : 'Inactivo'}
                    </span>
                    {!p.visible && <span className="badge badge--draft">Oculto</span>}
                  </div>
                </td>
                <td className="data-table__actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="ghost-btn small" onClick={() => setMode({ kind: 'edit', plan: p })}>
                    Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <p className="muted center">Cargando…</p>}
      </div>

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <PlanFormModal
          key={mode.kind === 'edit' ? mode.plan.id : 'new'}
          mode={mode.kind}
          plan={mode.kind === 'edit' ? mode.plan : null}
          onClose={() => setMode({ kind: 'list' })}
          onSaved={(saved) => {
            setItems((prev) => {
              const idx = prev.findIndex((p) => p.id === saved.id)
              if (idx === -1) return [...prev, saved].sort((a, b) => a.monthly_price - b.monthly_price)
              const next = [...prev]
              next[idx] = saved
              return next
            })
            setMode({ kind: 'list' })
          }}
        />
      )}
    </div>
  )
}

function limitLabel(v: number): string {
  if (v === -1) return 'Ilimitado'
  if (v === 0) return 'No incluido'
  return v.toLocaleString('es-MX')
}

function PlanFormModal({
  mode, plan, onClose, onSaved,
}: {
  mode: 'create' | 'edit'
  plan: AdminPlan | null
  onClose: () => void
  onSaved: (p: AdminPlan) => void
}) {
  const [name, setName] = useState(plan?.name ?? '')
  const [description, setDescription] = useState(plan?.description ?? '')
  const [monthlyPrice, setMonthlyPrice] = useState(String(plan?.monthly_price ?? '0'))
  const [yearlyPrice, setYearlyPrice] = useState(String(plan?.yearly_price ?? '0'))
  const [maxReports, setMaxReports] = useState(String(plan?.max_reports_per_month ?? '500'))
  const [maxAi, setMaxAi] = useState(String(plan?.max_ai_enhancements_per_month ?? '0'))
  const [maxClients, setMaxClients] = useState(String(plan?.max_clients ?? '-1'))
  const [maxUsers, setMaxUsers] = useState(String(plan?.max_users ?? '-1'))
  const [features, setFeatures] = useState<string[]>(plan?.features ?? [''])
  const [aiEnabled, setAiEnabled] = useState(plan?.ai_enhancement_enabled ?? false)
  const [active, setActive] = useState(plan?.active ?? true)
  const [visible, setVisible] = useState(plan?.visible ?? true)
  const [recommended, setRecommended] = useState(plan?.recommended ?? false)
  const [isDefault, setIsDefault] = useState(plan?.is_default ?? false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload: AdminPlanPayload = {
        name: name.trim(),
        description: description.trim(),
        monthly_price: parseFloat(monthlyPrice) || 0,
        yearly_price: parseFloat(yearlyPrice) || 0,
        max_reports_per_month: parseInt(maxReports, 10),
        max_ai_enhancements_per_month: parseInt(maxAi, 10),
        max_clients: parseInt(maxClients, 10),
        max_users: parseInt(maxUsers, 10),
        features: features.map((f) => f.trim()).filter((f) => f !== ''),
        ai_enhancement_enabled: aiEnabled,
        active,
        visible,
        recommended,
        is_default: isDefault,
      }
      const res = mode === 'create'
        ? await adminClient.planes.create(payload)
        : await adminClient.planes.update(plan!.id, payload)
      onSaved(res.plan)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={() => !submitting && onClose()}
      title={mode === 'create' ? 'Nuevo plan' : `Editar ${plan?.name}`}
      size="lg"
      dismissible={!submitting}
      footer={
        <div className="modal__actions">
          <button type="button" className="ghost-btn" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" form="plan-form" className="primary-btn" disabled={submitting}>
            {submitting ? 'Guardando…' : mode === 'create' ? 'Crear plan' : 'Guardar cambios'}
          </button>
        </div>
      }
    >
      <form id="plan-form" className="form-grid" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span>Nombre del plan</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required disabled={submitting} autoFocus />
        </label>

        <label className="field">
          <span>Descripción</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} disabled={submitting} />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Precio mensual (MXN)</span>
            <input type="number" step="0.01" value={monthlyPrice} onChange={(e) => setMonthlyPrice(e.target.value)} disabled={submitting} className="mono" />
          </label>
          <label className="field">
            <span>Precio anual (MXN)</span>
            <input type="number" step="0.01" value={yearlyPrice} onChange={(e) => setYearlyPrice(e.target.value)} disabled={submitting} className="mono" />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Reportes/mes <em className="muted">(-1 = ilimitado, 0 = no incluido)</em></span>
            <input type="number" value={maxReports} onChange={(e) => setMaxReports(e.target.value)} disabled={submitting} className="mono" />
          </label>
          <label className="field">
            <span>Mejoras IA/mes</span>
            <input type="number" value={maxAi} onChange={(e) => setMaxAi(e.target.value)} disabled={submitting} className="mono" />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Máx. clientes</span>
            <input type="number" value={maxClients} onChange={(e) => setMaxClients(e.target.value)} disabled={submitting} className="mono" />
          </label>
          <label className="field">
            <span>Máx. usuarios totales <em className="muted">(-1 = ilimitado)</em></span>
            <input type="number" value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} disabled={submitting} className="mono" />
          </label>
        </div>

        <div className="field">
          <span>Características <em className="muted">(una por línea, mostradas en la tarjeta del plan)</em></span>
          {features.map((f, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '.4rem', marginTop: '.3rem' }}>
              <input
                value={f}
                onChange={(e) => setFeatures((prev) => prev.map((v, i) => i === idx ? e.target.value : v))}
                placeholder="ej. Hasta 500 reportes/mes"
                disabled={submitting}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="ghost-btn small"
                onClick={() => setFeatures((prev) => prev.filter((_, i) => i !== idx))}
                disabled={submitting}
              >
                Quitar
              </button>
            </div>
          ))}
          <button
            type="button"
            className="ghost-btn small"
            onClick={() => setFeatures((prev) => [...prev, ''])}
            style={{ marginTop: '.4rem', alignSelf: 'flex-start' }}
            disabled={submitting}
          >
            + Agregar característica
          </button>
        </div>

        <div className="form-grid" style={{ gap: '.4rem', padding: '.5rem 0' }}>
          <ToggleLine label="Habilitar mejora de redacción con IA" checked={aiEnabled} onChange={setAiEnabled} disabled={submitting} />
          <ToggleLine label="Plan activo" checked={active} onChange={setActive} disabled={submitting} />
          <ToggleLine label="Visible en la pantalla pública de planes" checked={visible} onChange={setVisible} disabled={submitting} />
          <ToggleLine label="Marcar como 'Recomendado'" checked={recommended} onChange={setRecommended} disabled={submitting} />
          <ToggleLine label="Plan por defecto para nuevos usuarios" checked={isDefault} onChange={setIsDefault} disabled={submitting} />
        </div>

        {error && <p className="error">{error}</p>}
      </form>
    </Modal>
  )
}

function ToggleLine({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="toggle-row">
      <div className="toggle-row__text"><strong>{label}</strong></div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="toggle-switch"
      />
    </label>
  )
}
